import { z } from 'zod';
import {
  DEFAULT_AUTH_RELATED_NOISE_DOMAINS,
  DEFAULT_OAST_CALLBACK_DOMAINS,
  DEFAULT_OAUTH_IDENTITY_NOISE_DOMAINS,
} from './built-in-noise';
import {
  resolveExclusiveDomainScopeFlags,
  splitDomainHostAndPath,
  type DomainScopeFlags,
} from './domain-scope';
import {
  isHostExactlyOrSubdomainOf,
  isSubdomain,
} from './url-pattern-matching';

const KNOWN_THIRD_PARTY_DOMAINS: readonly string[] = [
  ...DEFAULT_AUTH_RELATED_NOISE_DOMAINS,
  ...DEFAULT_OAUTH_IDENTITY_NOISE_DOMAINS,
  ...DEFAULT_OAST_CALLBACK_DOMAINS,
];

type ShouldAutoMarkThirdPartyParams = {
  host: string;
  primaryHost?: string | null;
  isInTestingScope?: boolean | null;
  isInFunctionalityScope?: boolean | null;
  isThirdParty?: boolean | null;
};

function hostMatchesDomainList({
  host,
  domains,
}: {
  host: string;
  domains: readonly string[];
}): boolean {
  const normalized = host.toLowerCase();

  for (const domain of domains) {
    if (normalized === domain || isSubdomain(normalized, domain)) {
      return true;
    }
  }

  return false;
}

export function isKnownThirdPartyHost(host: string): boolean {
  return hostMatchesDomainList({
    host,
    domains: KNOWN_THIRD_PARTY_DOMAINS,
  });
}

const hostWithoutWww = (host: string): string => {
  const normalized = host.toLowerCase();

  return normalized.startsWith('www.') ? normalized.slice(4) : normalized;
};

const belongsToPrimaryHost = ({
  host,
  primaryHost,
}: {
  host: string;
  primaryHost: string;
}): boolean => {
  const primary = hostWithoutWww(primaryHost);
  const candidate = hostWithoutWww(host);

  return (
    isHostExactlyOrSubdomainOf(primary, candidate) ||
    isHostExactlyOrSubdomainOf(candidate, primary)
  );
};

export function shouldAutoMarkThirdParty({
  host,
  primaryHost,
  isInTestingScope,
  isInFunctionalityScope,
  isThirdParty,
}: ShouldAutoMarkThirdPartyParams): boolean {
  const { host: bareHost } = splitDomainHostAndPath(host);

  if (!isKnownThirdPartyHost(bareHost)) {
    return false;
  }

  if (primaryHost && belongsToPrimaryHost({ host: bareHost, primaryHost })) {
    return false;
  }

  if (isInTestingScope || isInFunctionalityScope) {
    return false;
  }

  if (isThirdParty != null) {
    return false;
  }

  return true;
}

function shouldBackfillAutoMarkThirdParty({
  host,
  primaryHost,
}: {
  host: string;
  primaryHost?: string | null;
}): boolean {
  const { host: bareHost } = splitDomainHostAndPath(host);

  if (!isKnownThirdPartyHost(bareHost)) {
    return false;
  }

  if (primaryHost && belongsToPrimaryHost({ host: bareHost, primaryHost })) {
    return false;
  }

  return true;
}

export function resolveBackfillCatalogThirdPartyFlags({
  host,
  primaryHost,
  isInFunctionalityScope,
  isInTestingScope,
  isThirdParty,
}: ShouldAutoMarkThirdPartyParams): DomainScopeFlags {
  const exclusive = resolveExclusiveDomainScopeFlags({
    isInFunctionalityScope,
    isInTestingScope,
    isThirdParty,
  });

  if (exclusive.isInTestingScope) {
    return exclusive;
  }

  if (!shouldBackfillAutoMarkThirdParty({ host, primaryHost })) {
    return exclusive;
  }

  return resolveExclusiveDomainScopeFlags({
    isThirdParty: true,
  });
}

export const BackfillDomainScopeTripleSchema = z.object({
  domain: z.string(),
  isInFunctionalityScope: z.boolean().nullable().optional(),
  isInTestingScope: z.boolean().nullable().optional(),
  isThirdParty: z.boolean().nullable().optional(),
});

export type BackfillDomainScopeTriple = z.infer<
  typeof BackfillDomainScopeTripleSchema
>;

const storedTripleDiffersFromResolved = ({
  entry,
  resolved,
}: {
  entry: BackfillDomainScopeTriple;
  resolved: DomainScopeFlags;
}): boolean =>
  (entry.isInFunctionalityScope ?? false) !== resolved.isInFunctionalityScope ||
  (entry.isInTestingScope ?? false) !== resolved.isInTestingScope ||
  (entry.isThirdParty ?? false) !== resolved.isThirdParty;

export function applyBackfillCatalogThirdPartyFlags<
  T extends BackfillDomainScopeTriple,
>({
  domains,
  primaryHost,
}: {
  domains: T[];
  primaryHost?: string | null;
}): { domains: T[]; changed: boolean } {
  let changed = false;

  const next = domains.map((entry) => {
    const parsed = BackfillDomainScopeTripleSchema.parse(entry);
    const resolved = resolveBackfillCatalogThirdPartyFlags({
      host: parsed.domain,
      primaryHost,
      isInFunctionalityScope: parsed.isInFunctionalityScope,
      isInTestingScope: parsed.isInTestingScope,
      isThirdParty: parsed.isThirdParty,
    });

    if (!storedTripleDiffersFromResolved({ entry: parsed, resolved })) {
      return entry;
    }

    changed = true;

    return {
      ...entry,
      ...resolved,
    };
  });

  return { domains: next, changed };
}
